/* eslint-disable @direct-frontend/modules/slice-boundaries -- подумать как уйти от соседних зависимостей */
import { useMemo } from 'react';
import { createStaticDataProvider } from '@adv-frontend/data-provider';
import {
    createFilterSelectWithSuggest,
    createRangeFilter,
    type Filter,
    type FiltersDeclaration,
    HighlightedText,
} from '@adv-frontend/filters';
import { type ADDON_FILTER_SLUG, createAddonFilter } from '#entities/addon';
import { type BUNDLE_FILTER_SLUG } from '#entities/bundle';
import { type CATEGORY_FILTER_ID, createCategoryFilter } from '#entities/category';
import { createCharacteristicFilter, useGetCharacteristicValuesBySlug } from '#entities/characteristic';
import { createDiscountFilter } from '#entities/discount';
import {
    type OpenApiSchemaABCCombinedCategory,
    type OpenApiSchemaCharacteristicMeta,
    type OpenApiSchemaCharacteristicMetaType,
    type OpenApiSchemaPriceIndexMarketplace,
} from '#shared/api/schema';
import { filterEmpty } from '#shared/lib/array';
import { type DISCOUNT_FILTER_ID } from '#shared/lib/filters';
import { useExperimentOrderSettingsFull } from '#shared/lib/flags';
import { checkIsLiteStore, checkIsNotLiteStore } from '#shared/lib/storeInfo';
import {
    BRAND_FILTER_TITLE,
    FINAL_PRICE_FILTER_ID,
    FINAL_PRICE_FILTER_TITLE,
    type HAS_MINIMAL_PRICE_FILTER_ID,
    HEIGHT_FILTER_ID,
    HEIGHT_FILTER_TITLE,
    LENGTH_FILTER_ID,
    LENGTH_FILTER_TITLE,
    MANUAL_DISCOUNT_PRICE_FILTER_ID,
    MANUAL_DISCOUNT_PRICE_FILTER_TITLE,
    PRICE_FILTER_ID,
    PRICE_FILTER_TITLE,
    type PRICE_INDEX_CHEAPEST_MARKETPLACE_FILTER_ID,
    PRICE_INDEX_DIFFERENCE_FILTER_ID,
    PRICE_INDEX_DIFFERENCE_FILTER_TITLE,
    PRICE_INDEX_PERCENT_FILTER_ID,
    PRICE_INDEX_PERCENT_FILTER_TITLE,
    PRODUCT_FILTER_BRAND_ID,
    PROMOTION_PRICE_FILTER_ID,
    PROMOTION_PRICE_FILTER_TITLE,
    type SORT_WEIGHT_FILTER_ID,
    TOTAL_QUANTITY_FILTER_ID,
    TOTAL_QUANTITY_FILTER_TITLE,
    type VAT_FILTER_ID,
    WEIGHT_FILTER_ID,
    WEIGHT_FILTER_TITLE,
    WIDTH_FILTER_ID,
    WIDTH_FILTER_TITLE,
} from '../constants';
import { type ABC_CATEGORIES_FILTER_ID } from '../lib/abcAnalysis';
import {
    createAbcCategoriesFilter,
    createBadgesFilter,
    createFeedbackSourceFilter,
    createHasGroupingCharacteristicsFilter,
    createHasMediaFilter,
    createHasMinimalPriceFilter,
    createRequiresMarkingFilter,
    createSortWeightFilter,
    createVatFilter,
    createWarehousesFilter,
    type HasMinimalPriceFilterId,
} from '../lib/filters';
import { createPriceIndexCheapestMarketplaceFilter } from '../lib/filters/createPriceIndexCheapestMarketplaceFilter';
import { createPriceIndexSignalsFilter } from '../lib/filters/createPriceIndexSignalsFilter';

const createFilterByCharacteristicType = (
    type: OpenApiSchemaCharacteristicMetaType,
    { title, slug, isNotUnique }: { title: string; slug: string; isNotUnique: boolean }
) => {
    if (type === 'INT') {
        return createRangeFilter({
            id: slug,
            title: isNotUnique ? `${title} (${slug})` : title,
            availableOperators: 'all',
            precision: 5,
        });
    }

    return createFilterSelectWithSuggest({
        id: slug,
        title: isNotUnique ? `${title} (${slug})` : title,
        suggestProps: {
            dataProvider: function Component(props) {
                const { data } = useGetCharacteristicValuesBySlug({
                    search_text: slug,
                });
                const search = props.queryArg.searchValue;

                const mappedData = useMemo(
                    () =>
                        (data?.values || []).map((value) => {
                            return {
                                value,
                                id: value,
                            };
                        }),
                    [data]
                );

                const filteredData = useMemo(
                    () =>
                        mappedData
                            .filter((item) => item.value.toLowerCase().includes(search.toLowerCase()))
                            // На dev есть искусственные значения, где 5к - 30к опций, их обрубаем, чтобы не провисали
                            .slice(0, 1000),
                    [mappedData, search]
                );

                const dataProvider = useMemo(() => createStaticDataProvider(filteredData), [filteredData]);

                return props.children(dataProvider);
            },
            useTitleByValue: (values: string[]) =>
                values.map((value) => {
                    return { value, title: value };
                }),
            renderSelectListItem: ({ item, search }) => <HighlightedText text={item.id} search={search} joinParts />,
            renderFilterChipsItem: ({ item }) => item.id,
        },
    });
};

export type ProductFilterDeclaration =
    // properties
    | Filter<typeof TOTAL_QUANTITY_FILTER_ID, string>
    | Filter<typeof PRICE_FILTER_ID, string>
    | Filter<typeof FINAL_PRICE_FILTER_ID, string>
    | Filter<typeof MANUAL_DISCOUNT_PRICE_FILTER_ID, string>
    | Filter<typeof PROMOTION_PRICE_FILTER_ID, string>
    | Filter<typeof VAT_FILTER_ID, string>
    | Filter<typeof SORT_WEIGHT_FILTER_ID, string>
    | Filter<typeof ADDON_FILTER_SLUG, string[]>
    | ReturnType<typeof createFeedbackSourceFilter>
    | Filter<typeof BUNDLE_FILTER_SLUG, string[]>
    | Filter<typeof ABC_CATEGORIES_FILTER_ID, OpenApiSchemaABCCombinedCategory[]>
    // before-characteristics
    | Filter<typeof PRODUCT_FILTER_BRAND_ID, string[]>
    | Filter<typeof DISCOUNT_FILTER_ID, string[]>
    | Filter<typeof CATEGORY_FILTER_ID, string[]>
    | Filter<typeof PRICE_INDEX_PERCENT_FILTER_ID, string>
    | Filter<typeof PRICE_INDEX_DIFFERENCE_FILTER_ID, string>
    | Filter<typeof PRICE_INDEX_CHEAPEST_MARKETPLACE_FILTER_ID, OpenApiSchemaPriceIndexMarketplace[]>
    | Filter<typeof HAS_MINIMAL_PRICE_FILTER_ID, HasMinimalPriceFilterId[]>
    // dimensions
    | Filter<typeof WIDTH_FILTER_ID, string>
    | Filter<typeof HEIGHT_FILTER_ID, string>
    | Filter<typeof WEIGHT_FILTER_ID, string>
    | Filter<typeof LENGTH_FILTER_ID, string>
    // rest characteristics
    | Filter<string, string>
    | Filter<string, string[]>
    // checkboxes
    | Filter<string, 'true' | 'false'>;

export type UseProductFiltersDeclarationOptions = {
    enableFilterCategory?: boolean;
    disableFilterFinalPrice?: boolean;
    isDynamicCollectionFilters?: boolean;
    disableFilterDiscounts?: boolean;
    disableFilterAddons?: boolean;
    disableFilterFeedbacks?: boolean;
    disableAbcAnalysis?: boolean;
};

export const useProductFiltersDeclaration = (
    characteristics: OpenApiSchemaCharacteristicMeta[] | undefined,
    options: UseProductFiltersDeclarationOptions = {}
) => {
    const isOrderSettingsFullEnabled = useExperimentOrderSettingsFull();
    const isDynamicCollectionFilters = options.isDynamicCollectionFilters ?? false;
    const isEnableFilterCategory = options.enableFilterCategory ?? false;
    const isDisableFilterFinalPrice = options.disableFilterFinalPrice ?? false;
    const isDisableFilterDiscounts = options.disableFilterDiscounts ?? false;
    const isDisableFilterAddons = options.disableFilterAddons ?? false;
    const isDisableFilterFeedbacks = options.disableFilterFeedbacks ?? false;
    const isDisableAbcAnalysis = options.disableAbcAnalysis ?? false;
    const propertiesDeclaration: ProductFilterDeclaration[] = useMemo(
        () =>
            [
                createRangeFilter({
                    id: TOTAL_QUANTITY_FILTER_ID,
                    title: TOTAL_QUANTITY_FILTER_TITLE,
                    availableOperators: 'all',
                }),
                isDisableFilterFinalPrice
                    ? undefined
                    : createRangeFilter({
                          id: FINAL_PRICE_FILTER_ID,
                          title: FINAL_PRICE_FILTER_TITLE,
                          availableOperators: 'all',
                      }),
                createRangeFilter({
                    id: PRICE_FILTER_ID,
                    title: PRICE_FILTER_TITLE,
                    availableOperators: 'all',
                }),
                createRangeFilter({
                    id: MANUAL_DISCOUNT_PRICE_FILTER_ID,
                    title: MANUAL_DISCOUNT_PRICE_FILTER_TITLE,
                    availableOperators: 'all',
                }),
                createRangeFilter({
                    id: PROMOTION_PRICE_FILTER_ID,
                    title: PROMOTION_PRICE_FILTER_TITLE,
                    availableOperators: 'all',
                }),
                createVatFilter(),
            ].filter(filterEmpty),
        [isDisableFilterFinalPrice]
    );

    const beforeCharacteristicsDeclaration: ProductFilterDeclaration[] = useMemo(() => {
        const filters: ProductFilterDeclaration[] = [
            createFilterByCharacteristicType('STRING', {
                title: BRAND_FILTER_TITLE,
                slug: PRODUCT_FILTER_BRAND_ID,
                isNotUnique: false,
            }),
            isDisableAbcAnalysis ? undefined : createAbcCategoriesFilter(),
            checkIsNotLiteStore() ? createBadgesFilter() : undefined,
            createCharacteristicFilter(),
            isDynamicCollectionFilters ? undefined : createHasMediaFilter(),
            isOrderSettingsFullEnabled && !isDynamicCollectionFilters ? createRequiresMarkingFilter() : undefined,
            isDynamicCollectionFilters ? undefined : createWarehousesFilter(),
            createSortWeightFilter(),
            isDisableFilterFeedbacks ? undefined : createFeedbackSourceFilter(),
            createPriceIndexSignalsFilter(),
            createPriceIndexCheapestMarketplaceFilter(),
            createRangeFilter({
                id: PRICE_INDEX_PERCENT_FILTER_ID,
                title: PRICE_INDEX_PERCENT_FILTER_TITLE,
                availableOperators: 'all',
            }),
            createRangeFilter({
                id: PRICE_INDEX_DIFFERENCE_FILTER_ID,
                title: PRICE_INDEX_DIFFERENCE_FILTER_TITLE,
                availableOperators: 'all',
            }),
            isDisableFilterDiscounts ? undefined : createDiscountFilter(),
            isEnableFilterCategory ? createCategoryFilter() : undefined,
            isDynamicCollectionFilters ? undefined : createHasGroupingCharacteristicsFilter(),
            isDisableFilterAddons || checkIsLiteStore() ? undefined : createAddonFilter(),
            createHasMinimalPriceFilter(),
        ].filter(filterEmpty);

        return filters;
    }, [
        isDisableAbcAnalysis,
        isOrderSettingsFullEnabled,
        isDisableFilterDiscounts,
        isEnableFilterCategory,
        isDynamicCollectionFilters,
        isDisableFilterAddons,
        isDisableFilterFeedbacks,
    ]);

    const afterCharacteristicsDeclaration: ProductFilterDeclaration[] = useMemo(
        () => [
            createRangeFilter({
                id: WEIGHT_FILTER_ID,
                title: WEIGHT_FILTER_TITLE,
                availableOperators: 'all',
            }),
            createRangeFilter({
                id: LENGTH_FILTER_ID,
                title: LENGTH_FILTER_TITLE,
                availableOperators: 'all',
            }),
            createRangeFilter({
                id: WIDTH_FILTER_ID,
                title: WIDTH_FILTER_TITLE,
                availableOperators: 'all',
            }),
            createRangeFilter({
                id: HEIGHT_FILTER_ID,
                title: HEIGHT_FILTER_TITLE,
                availableOperators: 'all',
            }),
        ],
        []
    );

    const characteristicsDeclaration: ProductFilterDeclaration[] = useMemo(
        () =>
            (characteristics || []).map(({ slug, title, type, is_unique_title: isUniqueTitle }) =>
                createFilterByCharacteristicType(type, { title, slug, isNotUnique: isUniqueTitle === false })
            ),
        [characteristics]
    );

    return useMemo((): FiltersDeclaration<ProductFilterDeclaration> => {
        return {
            dynamicFilters: propertiesDeclaration.concat(
                beforeCharacteristicsDeclaration,
                characteristicsDeclaration,
                afterCharacteristicsDeclaration
            ),
        };
    }, [
        propertiesDeclaration,
        beforeCharacteristicsDeclaration,
        characteristicsDeclaration,
        afterCharacteristicsDeclaration,
    ]);
};
